import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useCompany } from '../contexts/CompanyContext';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Plus, Search, Edit2, Trash2, User, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { UpgradeModal } from '../components/UpgradeModal';

const fadeIn = { initial: { opacity: 0, y: 10 }, animate: { opacity: 1, y: 0 } };

const defaultForm = {
  first_name: '', last_name: '', email: '', job_title: '', company_id: '', company_name: '', department: '',
  visa_type: 'H-1B', visa_status: 'active', lca_case_number: '', lca_filing_date: '', lca_expiration_date: '',
  wage_level: '', prevailing_wage_amount: '', offer_wage_amount: '', worksite_address: '', remote_address: '', soc_code: '', soc_title: '', notes: ''
};

export default function EmployeesPage() {
  const { apiCall, user } = useAuth();
  const { companies, selectedCompanyId } = useCompany();
  const [searchParams] = useSearchParams();
  const [employees, setEmployees] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState(() => searchParams.get('status') || '');
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(defaultForm);
  const [saving, setSaving] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [planUsage, setPlanUsage] = useState({});
  const [deleteTarget, setDeleteTarget] = useState(null);
  const canEdit = user?.role !== 'viewer';

  const fetchEmployees = useCallback(async () => {
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      if (search) params.set('search', search);
      if (statusFilter) params.set('status', statusFilter);
      if (selectedCompanyId) params.set('company_id', selectedCompanyId);
      const res = await apiCall('get', `/employees?${params}`);
      setEmployees(res.data.employees);
      setTotal(res.data.total);
    } catch (err) {
      toast.error('Failed to load employees');
    } finally {
      setLoading(false);
    }
  }, [apiCall, page, search, statusFilter, selectedCompanyId]);

  useEffect(() => { fetchEmployees(); }, [fetchEmployees]);

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editing) {
        await apiCall('put', `/employees/${editing}`, form);
        toast.success('Employee updated');
      } else {
        await apiCall('post', '/employees', form);
        toast.success('Employee created');
      }
      setShowDialog(false);
      setForm(defaultForm);
      setEditing(null);
      fetchEmployees();
    } catch (err) {
      if (err.response?.status === 403 && err.response?.data?.detail?.includes('limit')) {
        try {
          const res = await apiCall('get', '/plan/usage');
          setPlanUsage(res.data);
        } catch {}
        setShowUpgrade(true);
      } else {
        toast.error(err.response?.data?.detail || 'Failed to save');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (emp) => {
    setForm({
      first_name: emp.first_name, last_name: emp.last_name, email: emp.email,
      job_title: emp.job_title || '', company_id: emp.company_id || '', company_name: emp.company_name || '', department: emp.department || '',
      visa_type: emp.visa_type || 'H-1B', visa_status: emp.visa_status,
      lca_case_number: emp.lca_case_number || '', lca_filing_date: emp.lca_filing_date || '',
      lca_expiration_date: emp.lca_expiration_date || '', wage_level: emp.wage_level || '',
      worksite_address: emp.worksite_address || '',
      prevailing_wage_amount: emp.prevailing_wage_amount || '', offer_wage_amount: emp.offer_wage_amount || '',
      remote_address: emp.remote_address || '',
      soc_code: emp.soc_code || '', soc_title: emp.soc_title || '', notes: emp.notes || ''
    });
    setEditing(emp.id);
    setShowDialog(true);
  };

  const handleDelete = async (id, name) => {
    setDeleteTarget({ id, name });
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await apiCall('delete', `/employees/${deleteTarget.id}`);
      toast.success('Employee deleted');
      fetchEmployees();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    } finally {
      setDeleteTarget(null);
    }
  };

  const statusBadge = (status) => {
    const styles = {
      active: 'bg-emerald-50 text-emerald-600 border border-emerald-100',
      expired: 'bg-red-50 text-red-600 border border-red-100',
      pending: 'bg-amber-50 text-amber-600 border border-amber-100',
    };
    return (
      <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-semibold ${styles[status] || styles.pending}`}>
        {status}
      </span>
    );
  };

  return (
    <div data-testid="employees-page">
      <motion.div {...fadeIn} transition={{ duration: 0.3 }}>
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-[#0F172A] tracking-tight mb-1">Employees</h1>
            <p className="text-[#64748B]">Manage H-1B, H-1B1 & E-3 employee records and visa details</p>
          </div>
          {canEdit && (
            <Button
              data-testid="add-employee-btn"
              onClick={() => { setForm(defaultForm); setEditing(null); setShowDialog(true); }}
              className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl px-6 font-semibold shadow-md hover:shadow-lg hover:shadow-indigo-500/20 transition-all active:scale-[0.98]"
            >
              <Plus className="w-4 h-4 mr-2" /> Add Employee
            </Button>
          )}
        </div>
      </motion.div>

      {/* Filters */}
      <motion.div {...fadeIn} transition={{ duration: 0.3, delay: 0.05 }} className="flex items-center gap-4 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8]" />
          <Input
            data-testid="search-employees"
            placeholder="Search employees..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="pl-10 rounded-xl border-[#E2E8F0] bg-white h-11 focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 shadow-sm"
          />
        </div>
        <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v === 'all' ? '' : v); setPage(1); }}>
          <SelectTrigger className="w-44 rounded-xl border-[#E2E8F0] h-11 bg-white shadow-sm" data-testid="filter-status">
            <SelectValue placeholder="All Statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="expired">Expired</SelectItem>
          </SelectContent>
        </Select>
      </motion.div>

      {/* Table */}
      <motion.div {...fadeIn} transition={{ duration: 0.3, delay: 0.1 }}
        className="bg-white rounded-2xl border border-indigo-100/50 shadow-sm overflow-hidden"
      >
        {loading ? (
          <div className="flex items-center justify-center h-48">
            <div className="w-6 h-6 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : employees.length === 0 ? (
          <div className="text-center py-16">
            <User className="w-12 h-12 text-[#94A3B8] mx-auto mb-3" strokeWidth={1.5} />
            <p className="text-[#64748B]">No employees found</p>
            {canEdit && (
              <Button onClick={() => { setForm(defaultForm); setEditing(null); setShowDialog(true); }}
                className="mt-4 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl px-6"
              >
                Add your first employee
              </Button>
            )}
          </div>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow className="border-b border-indigo-50">
                  <TableHead className="text-[#64748B] font-semibold text-xs uppercase tracking-wider">Name</TableHead>
                  <TableHead className="text-[#64748B] font-semibold text-xs uppercase tracking-wider">Email</TableHead>
                  <TableHead className="text-[#64748B] font-semibold text-xs uppercase tracking-wider">Company</TableHead>
                  <TableHead className="text-[#64748B] font-semibold text-xs uppercase tracking-wider">Visa Type</TableHead>
                  <TableHead className="text-[#64748B] font-semibold text-xs uppercase tracking-wider">Visa Status</TableHead>
                  <TableHead className="text-[#64748B] font-semibold text-xs uppercase tracking-wider">LCA Expiration</TableHead>
                  {canEdit && <TableHead className="text-[#64748B] font-semibold text-xs uppercase tracking-wider text-right">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {employees.map((emp) => (
                  <TableRow key={emp.id} className="border-b border-indigo-50/50 hover:bg-indigo-50/30 transition-colors cursor-pointer" data-testid={`employee-row-${emp.id}`} onClick={() => handleEdit(emp)}>
                    <TableCell className="font-semibold text-[#0F172A]">
                      {emp.first_name} {emp.last_name}
                    </TableCell>
                    <TableCell className="text-[#64748B]">{emp.email}</TableCell>
                    <TableCell className="text-[#64748B]">{emp.company_name || '—'}</TableCell>
                    <TableCell>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-50 text-indigo-700 border border-indigo-100">
                        {emp.visa_type || 'H-1B'}
                      </span>
                    </TableCell>
                    <TableCell>{statusBadge(emp.visa_status)}</TableCell>
                    <TableCell className="text-[#64748B]">{emp.lca_expiration_date || '—'}</TableCell>
                    {canEdit && (
                      <TableCell className="text-right" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => handleEdit(emp)} className="p-2 rounded-lg hover:bg-indigo-50 text-[#94A3B8] hover:text-indigo-600 transition-colors" data-testid={`edit-employee-${emp.id}`}>
                            <Edit2 className="w-4 h-4" strokeWidth={1.5} />
                          </button>
                          {user?.role === 'admin' && (
                            <button onClick={() => handleDelete(emp.id, `${emp.first_name} ${emp.last_name}`)} className="p-2 rounded-lg hover:bg-red-50 text-[#94A3B8] hover:text-red-500 transition-colors" data-testid={`delete-employee-${emp.id}`}>
                              <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                            </button>
                          )}
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {total > 20 && (
              <div className="flex items-center justify-between p-4 border-t border-indigo-50">
                <span className="text-sm text-[#64748B]">Showing {(page-1)*20+1}–{Math.min(page*20, total)} of {total}</span>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage(p => p-1)} className="rounded-lg border-[#E2E8F0]"><ChevronLeft className="w-4 h-4" /></Button>
                  <Button variant="outline" size="sm" disabled={page*20 >= total} onClick={() => setPage(p => p+1)} className="rounded-lg border-[#E2E8F0]"><ChevronRight className="w-4 h-4" /></Button>
                </div>
              </div>
            )}
          </>
        )}
      </motion.div>

      {/* Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white rounded-2xl" data-testid="employee-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#0F172A] tracking-tight">
              {editing ? 'Edit Employee' : 'Add Employee'}
            </DialogTitle>
            <DialogDescription className="text-sm text-[#64748B]">
              {editing ? 'Update employee details and visa information' : 'Add a new H-1B, H-1B1, or E-3 employee to the system'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-6 mt-4">
            {/* Personal Information */}
            <div>
              <div className="flex items-center gap-2 mb-3 pb-2 border-b border-[#E2E8F0]">
                <div className="w-1.5 h-1.5 rounded-full bg-[#4F46E5]" />
                <p className="text-xs font-semibold text-[#4F46E5] uppercase tracking-wider">Personal Information</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-[#0F172A] mb-1.5 block">First Name *</Label>
                  <Input data-testid="emp-first-name" value={form.first_name} onChange={e => setForm({...form, first_name: e.target.value})} className="rounded-xl border-[#E2E8F0] bg-white h-11 shadow-sm" />
                </div>
                <div>
                  <Label className="text-sm font-medium text-[#0F172A] mb-1.5 block">Last Name *</Label>
                  <Input data-testid="emp-last-name" value={form.last_name} onChange={e => setForm({...form, last_name: e.target.value})} className="rounded-xl border-[#E2E8F0] bg-white h-11 shadow-sm" />
                </div>
                <div className="col-span-2">
                  <Label className="text-sm font-medium text-[#0F172A] mb-1.5 block">Email *</Label>
                  <Input data-testid="emp-email" type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="rounded-xl border-[#E2E8F0] bg-white h-11 shadow-sm" />
                </div>
              </div>
            </div>

            {/* Employment */}
            <div>
              <div className="flex items-center gap-2 mb-3 pb-2 border-b border-[#E2E8F0]">
                <div className="w-1.5 h-1.5 rounded-full bg-[#4F46E5]" />
                <p className="text-xs font-semibold text-[#4F46E5] uppercase tracking-wider">Employment</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-[#0F172A] mb-1.5 block">Company *</Label>
                  {companies.length > 0 ? (
                    <Select value={form.company_id} onValueChange={v => {
                      const c = companies.find(co => co.id === v);
                      setForm({...form, company_id: v, company_name: c ? c.name : ''});
                    }}>
                      <SelectTrigger className="rounded-xl border-[#E2E8F0] h-11 bg-white shadow-sm" data-testid="emp-company-select"><SelectValue placeholder="Select company" /></SelectTrigger>
                      <SelectContent>
                        {companies.map(c => (
                          <SelectItem key={c.id} value={c.id}>{c.name}{c.ein ? ` (${c.ein})` : ''}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input data-testid="emp-company-name" value={form.company_name} onChange={e => setForm({...form, company_name: e.target.value})} placeholder="e.g. Acme Corp" className="rounded-xl border-[#E2E8F0] bg-white h-11 shadow-sm" />
                  )}
                </div>
                <div>
                  <Label className="text-sm font-medium text-[#0F172A] mb-1.5 block">Department</Label>
                  <Input data-testid="emp-department" value={form.department} onChange={e => setForm({...form, department: e.target.value})} placeholder="Optional" className="rounded-xl border-[#E2E8F0] bg-white h-11 shadow-sm" />
                </div>
              </div>
            </div>

            {/* Job Classification */}
            <div>
              <div className="flex items-center gap-2 mb-3 pb-2 border-b border-[#E2E8F0]">
                <div className="w-1.5 h-1.5 rounded-full bg-[#4F46E5]" />
                <p className="text-xs font-semibold text-[#4F46E5] uppercase tracking-wider">Job Classification</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-[#0F172A] mb-1.5 block">Job Title</Label>
                  <Input data-testid="emp-job-title" value={form.job_title} onChange={e => setForm({...form, job_title: e.target.value})} className="rounded-xl border-[#E2E8F0] bg-white h-11 shadow-sm" />
                </div>
                <div>
                  <Label className="text-sm font-medium text-[#0F172A] mb-1.5 block">SOC Code</Label>
                  <Input data-testid="emp-soc-code" value={form.soc_code} onChange={e => setForm({...form, soc_code: e.target.value})} className="rounded-xl border-[#E2E8F0] bg-white h-11 shadow-sm" />
                </div>
                <div className="col-span-2">
                  <Label className="text-sm font-medium text-[#0F172A] mb-1.5 block">SOC Job Title</Label>
                  <Input data-testid="emp-soc-title" value={form.soc_title} onChange={e => setForm({...form, soc_title: e.target.value})} className="rounded-xl border-[#E2E8F0] bg-white h-11 shadow-sm" />
                </div>
              </div>
            </div>

            {/* Visa & LCA */}
            <div>
              <div className="flex items-center gap-2 mb-3 pb-2 border-b border-[#E2E8F0]">
                <div className="w-1.5 h-1.5 rounded-full bg-[#4F46E5]" />
                <p className="text-xs font-semibold text-[#4F46E5] uppercase tracking-wider">Visa & LCA</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-[#0F172A] mb-1.5 block">Visa Type *</Label>
                  <Select value={form.visa_type} onValueChange={v => setForm({...form, visa_type: v})}>
                    <SelectTrigger className="rounded-xl border-[#E2E8F0] h-11 bg-white shadow-sm" data-testid="emp-visa-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="H-1B">H-1B</SelectItem>
                      <SelectItem value="H-1B1 (Chile/Singapore)">H-1B1 (Chile/Singapore)</SelectItem>
                      <SelectItem value="E-3 (Australia)">E-3 (Australia)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm font-medium text-[#0F172A] mb-1.5 block">Visa Status</Label>
                  <Select value={form.visa_status} onValueChange={v => setForm({...form, visa_status: v})}>
                    <SelectTrigger className="rounded-xl border-[#E2E8F0] h-11 bg-white shadow-sm" data-testid="emp-visa-status"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="expired">Expired</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm font-medium text-[#0F172A] mb-1.5 block">LCA Case Number</Label>
                  <Input data-testid="emp-lca-case" value={form.lca_case_number} onChange={e => setForm({...form, lca_case_number: e.target.value})} className="rounded-xl border-[#E2E8F0] bg-white h-11 shadow-sm" />
                </div>
                <div>
                  <Label className="text-sm font-medium text-[#0F172A] mb-1.5 block">LCA Filing Date</Label>
                  <Input data-testid="emp-lca-filing" type="date" value={form.lca_filing_date} onChange={e => setForm({...form, lca_filing_date: e.target.value})} className="rounded-xl border-[#E2E8F0] bg-white h-11 shadow-sm" />
                </div>
                <div>
                  <Label className="text-sm font-medium text-[#0F172A] mb-1.5 block">LCA Expiration Date</Label>
                  <Input data-testid="emp-lca-expiration" type="date" value={form.lca_expiration_date} onChange={e => setForm({...form, lca_expiration_date: e.target.value})} className="rounded-xl border-[#E2E8F0] bg-white h-11 shadow-sm" />
                </div>
              </div>
            </div>

            {/* Compensation & Location */}
            <div>
              <div className="flex items-center gap-2 mb-3 pb-2 border-b border-[#E2E8F0]">
                <div className="w-1.5 h-1.5 rounded-full bg-[#4F46E5]" />
                <p className="text-xs font-semibold text-[#4F46E5] uppercase tracking-wider">Compensation & Location</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-[#0F172A] mb-1.5 block">Wage Level</Label>
                  <Select value={form.wage_level || 'none'} onValueChange={v => setForm({...form, wage_level: v === 'none' ? '' : v})}>
                    <SelectTrigger className="rounded-xl border-[#E2E8F0] h-11 bg-white shadow-sm" data-testid="emp-wage-level"><SelectValue placeholder="Select level" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Select level</SelectItem>
                      <SelectItem value="Level I">Level I</SelectItem>
                      <SelectItem value="Level II">Level II</SelectItem>
                      <SelectItem value="Level III">Level III</SelectItem>
                      <SelectItem value="Level IV">Level IV</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm font-medium text-[#0F172A] mb-1.5 block">Prevailing Wage Amount</Label>
                  <Input data-testid="emp-prevailing-wage" value={form.prevailing_wage_amount} onChange={e => setForm({...form, prevailing_wage_amount: e.target.value})} placeholder="e.g. $110,000" className="rounded-xl border-[#E2E8F0] bg-white h-11 shadow-sm" />
                </div>
                <div>
                  <Label className="text-sm font-medium text-[#0F172A] mb-1.5 block">Offer Wage Amount</Label>
                  <Input data-testid="emp-offer-wage" value={form.offer_wage_amount} onChange={e => setForm({...form, offer_wage_amount: e.target.value})} placeholder="e.g. $125,000" className="rounded-xl border-[#E2E8F0] bg-white h-11 shadow-sm" />
                </div>
                <div className="col-span-2">
                  <Label className="text-sm font-medium text-[#0F172A] mb-1.5 block">Worksite Address</Label>
                  <Input data-testid="emp-worksite" value={form.worksite_address} onChange={e => setForm({...form, worksite_address: e.target.value})} className="rounded-xl border-[#E2E8F0] bg-white h-11 shadow-sm" />
                </div>
                <div className="col-span-2">
                  <Label className="text-sm font-medium text-[#0F172A] mb-1.5 block">Remote Address</Label>
                  <Input data-testid="emp-remote-address" value={form.remote_address} onChange={e => setForm({...form, remote_address: e.target.value})} className="rounded-xl border-[#E2E8F0] bg-white h-11 shadow-sm" />
                </div>
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-3 mt-6">
            <Button variant="outline" onClick={() => setShowDialog(false)} className="rounded-xl px-6 border-[#E2E8F0]" data-testid="cancel-employee-btn">Cancel</Button>
            <Button onClick={handleSave} disabled={saving || !form.first_name || !form.last_name || !form.email || !form.company_name}
              className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl px-6 shadow-md hover:shadow-lg hover:shadow-indigo-500/20" data-testid="save-employee-btn"
            >
              {saving ? 'Saving...' : editing ? 'Update' : 'Create'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <UpgradeModal
        open={showUpgrade}
        onClose={() => setShowUpgrade(false)}
        limitType="employee"
        currentPlan={planUsage.account_type || 'free'}
        usage={planUsage}
      />

      {/* Delete Confirmation */}
      <Dialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <DialogContent className="max-w-sm bg-white rounded-2xl" data-testid="delete-employee-dialog">
          <div className="text-center pt-2">
            <div className="flex items-center justify-center gap-1.5 mb-5">
              <img src="/logo.svg" alt="PAFHub" className="w-7 h-7" />
              <span className="font-bold text-sm tracking-tight"><span className="text-[#000000]">PAF</span><span className="text-[#4F46E5]">Hub</span></span>
            </div>
            <div className="w-14 h-14 rounded-full bg-red-50 border border-red-100 flex items-center justify-center mx-auto mb-4">
              <Trash2 className="w-6 h-6 text-red-500" strokeWidth={1.5} />
            </div>
            <DialogHeader>
              <DialogTitle className="text-lg font-bold text-[#0F172A] tracking-tight">Delete Employee</DialogTitle>
              <DialogDescription className="text-sm text-[#64748B] mt-2">
                Are you sure you want to delete <span className="font-semibold text-[#0F172A]">"{deleteTarget?.name}"</span>? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <div className="flex gap-3 mt-6">
              <Button
                variant="outline"
                onClick={() => setDeleteTarget(null)}
                className="flex-1 rounded-xl border-[#E2E8F0] h-11"
                data-testid="cancel-delete-employee-btn"
              >
                Cancel
              </Button>
              <Button
                onClick={confirmDelete}
                className="flex-1 rounded-xl bg-red-600 hover:bg-red-700 text-white h-11"
                data-testid="confirm-delete-employee-btn"
              >
                Delete
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
